import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getFirebaseApp, getFirestoreDb } from './firebase';
import type { Unsubscribe } from 'firebase/firestore';
import { createLogger } from './loggingService';

const logger = createLogger('StripeService');

type FirestoreModule = typeof import('firebase/firestore');
let firestoreModulePromise: Promise<FirestoreModule> | null = null;

const loadFirestoreModule = () => {
  if (!firestoreModulePromise) {
    firestoreModulePromise = import('firebase/firestore');
  }
  return firestoreModulePromise;
};

const getFirestoreDeps = async () => {
  const [module, db] = await Promise.all([loadFirestoreModule(), getFirestoreDb()]);
  return { module, db };
};

// Initialize Stripe
let stripePromise: Promise<Stripe | null>;
const getStripe = () => {
  if (!stripePromise) {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      logger.error('Missing VITE_STRIPE_PUBLISHABLE_KEY');
      return Promise.resolve(null);
    }
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

// Pricing tier configuration
export const PRICING_CONFIG = {
  starter: {
    priceId: import.meta.env.VITE_STRIPE_PRICE_STARTER || 'price_starter_id_here',
    name: 'Starter',
    price: 10,
    asinLimit: 250,
  },
  growth: {
    priceId: import.meta.env.VITE_STRIPE_PRICE_GROWTH || 'price_growth_id_here',
    name: 'Growth',
    price: 19,
    asinLimit: 750,
  },
  enterprise: {
    priceId: import.meta.env.VITE_STRIPE_PRICE_ENTERPRISE || 'price_enterprise_id_here',
    name: 'Enterprise',
    price: 49,
    asinLimit: Infinity,
  },
} as const;

export type PricingTier = keyof typeof PRICING_CONFIG;

// Usage tier configuration for one-time purchases
export const USAGE_TIERS = {
  basic: {
    priceId: import.meta.env.VITE_STRIPE_PRICE_USAGE_BASIC || 'price_usage_basic_id_here',
    name: '7 Analysis Runs',
    runs: 7,
    price: 10,
  },
  standard: {
    priceId: import.meta.env.VITE_STRIPE_PRICE_USAGE_STANDARD || 'price_usage_standard_id_here',
    name: '20 Analysis Runs',
    runs: 20,
    price: 19,
  },
  premium: {
    priceId: import.meta.env.VITE_STRIPE_PRICE_USAGE_PREMIUM || 'price_usage_premium_id_here',
    name: '50 Analysis Runs',
    runs: 50,
    price: 40,
  },
} as const;

export type UsageTier = keyof typeof USAGE_TIERS;

export interface SubscriptionData {
  customerId: string;
  subscriptionId: string;
  status: 'active' | 'trialing' | 'canceled' | 'past_due' | 'unpaid' | 'incomplete' | 'incomplete_expired' | 'paused';
  tier: PricingTier;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  trialEnd: number | null;
}

export interface UsageData {
  freeRunsRemaining: number;
  paidRunsRemaining: number;
  totalRunsUsed: number;
  lastRunAt: number | null;
  purchaseHistory: Array<{
    tierId: UsageTier;
    runsAdded: number;
    purchasedAt: number;
    amount: number;
  }>;
}

/**
 * Create a Stripe Checkout Session
 * This will redirect the user to Stripe's hosted checkout page
 */
export const createCheckoutSession = async (
  tier: PricingTier,
  userId: string,
  userEmail: string
): Promise<void> => {
  const priceId = PRICING_CONFIG[tier].priceId;
  
  if (!priceId || priceId.includes('_here')) {
    throw new Error('Stripe price IDs not configured. Please set up Stripe products in your dashboard.');
  }

  try {
    // In production, this should call your backend endpoint
    // For now, we'll use Firestore to trigger a Cloud Function
    const { module, db } = await getFirestoreDeps();
    const checkoutSessionRef = module.doc(db, 'users', userId, 'checkout_sessions', `session_${Date.now()}`);
    
    await module.setDoc(checkoutSessionRef, {
      priceId,
      success_url: `${window.location.origin}/checkout/success`,
      cancel_url: `${window.location.origin}/checkout/canceled`,
      customer_email: userEmail,
      trial_period_days: 7,
      allow_promotion_codes: true,
      mode: 'subscription',
      created: new Date().toISOString(),
    });

    // In a real implementation, a Cloud Function would process this and create the session
    // Then update the document with the sessionId, which we'd use to redirect
    // For now, we'll show an error that the webhook/cloud function needs to be set up
    throw new Error('Cloud Function for Checkout Session not yet deployed. See plan for implementation.');
    
  } catch (error) {
    logger.error('Error creating checkout session', { error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
};

/**
 * Alternative: Direct API call to create checkout session
 * Uses Firebase Cloud Functions
 */
export const createCheckoutSessionViaAPI = async (
  tier: PricingTier,
  userId: string,
  userEmail: string
): Promise<void> => {
  const priceId = PRICING_CONFIG[tier].priceId;
  
  if (!priceId || priceId.includes('_here')) {
    throw new Error('Stripe price IDs not configured. Please set up Stripe products in your dashboard.');
  }

  try {
    // Import Firebase Functions
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const app = await getFirebaseApp();
    const functions = getFunctions(app);
    
    // Call the Cloud Function
    const createCheckoutSessionFn = httpsCallable(functions, 'createCheckoutSession');
    const result = await createCheckoutSessionFn({
      priceId,
      successUrl: `${window.location.origin}/checkout/success`,
      cancelUrl: `${window.location.origin}/checkout/canceled`,
    });

    const data = result.data as { sessionId: string; url: string };
    
    // Redirect to Stripe Checkout using the URL
    if (data.url) {
      window.location.href = data.url;
    } else if (data.sessionId) {
      // Fallback: use Stripe.js to redirect
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }

      const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
      
      if (error) {
        throw error;
      }
    }
  } catch (error) {
    logger.error('Error creating checkout session via API', { error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
};

/**
 * Create a Customer Portal session
 * Uses Firebase Cloud Functions
 */
export const createCustomerPortalSession = async (customerId: string): Promise<void> => {
  try {
    // Import Firebase Functions
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const app = await getFirebaseApp();
    const functions = getFunctions(app);
    
    // Call the Cloud Function
    const createPortalSessionFn = httpsCallable(functions, 'createPortalSession');
    const result = await createPortalSessionFn({
      returnUrl: `${window.location.origin}/settings/subscription`,
    });

    const data = result.data as { url: string };
    
    // Redirect to Stripe Customer Portal
    window.location.href = data.url;
  } catch (error) {
    logger.error('Error creating portal session', { error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
};

/**
 * Get subscription status from Firestore
 */
export const getSubscriptionStatus = async (userId: string): Promise<SubscriptionData | null> => {
  try {
    const { module, db } = await getFirestoreDeps();
    const subscriptionRef = module.doc(db, 'users', userId, 'subscription', 'current');
    const subscriptionSnap = await module.getDoc(subscriptionRef);
    
    if (!subscriptionSnap.exists()) {
      return null;
    }
    
    return subscriptionSnap.data() as SubscriptionData;
  } catch (error) {
    logger.error('Error fetching subscription status', { error: error instanceof Error ? error.message : String(error), userId });
    return null;
  }
};

/**
 * Subscribe to real-time subscription updates
 */
export const subscribeToSubscriptionUpdates = (
  userId: string,
  callback: (subscription: SubscriptionData | null) => void
): Unsubscribe => {
  let unsubscribe: Unsubscribe | null = null;

  const start = async () => {
    const { module, db } = await getFirestoreDeps();
    const subscriptionRef = module.doc(db, 'users', userId, 'subscription', 'current');
    unsubscribe = module.onSnapshot(
      subscriptionRef,
      snapshot => {
        if (snapshot.exists()) {
          callback(snapshot.data() as SubscriptionData);
        } else {
          callback(null);
        }
      },
      error => {
        logger.error('Error subscribing to subscription updates', { error: error instanceof Error ? error.message : String(error) });
        callback(null);
      }
    );
  };

  start().catch(error => {
    logger.error('Failed to subscribe to subscription updates', { error: error instanceof Error ? error.message : String(error) });
    callback(null);
  });

  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
};

/**
 * Check if user has access to a feature based on their subscription tier
 */
export const hasFeatureAccess = (
  subscription: SubscriptionData | null,
  feature: 'verification' | 'portfolio-health' | 'advanced-features'
): boolean => {
  if (!subscription || !['active', 'trialing'].includes(subscription.status)) {
    return false;
  }

  switch (feature) {
    case 'verification':
      return ['growth', 'enterprise'].includes(subscription.tier);
    case 'portfolio-health':
      return ['growth', 'enterprise'].includes(subscription.tier);
    case 'advanced-features':
      return subscription.tier === 'enterprise';
    default:
      return false;
  }
};

/**
 * Check ASIN limit for subscription tier
 */
export const checkAsinLimit = (
  subscription: SubscriptionData | null,
  asinCount: number
): { allowed: boolean; limit: number; tier: string } => {
  if (!subscription || !['active', 'trialing'].includes(subscription.status)) {
    return { allowed: false, limit: 0, tier: 'none' };
  }

  const config = PRICING_CONFIG[subscription.tier];
  return {
    allowed: asinCount <= config.asinLimit,
    limit: config.asinLimit,
    tier: config.name,
  };
};

/**
 * Create a Stripe Checkout Session for one-time usage tier purchase
 */
export const createUsageTierCheckoutSession = async (
  tier: UsageTier,
  userId: string,
  userEmail: string
): Promise<void> => {
  const priceId = USAGE_TIERS[tier].priceId;
  
  if (!priceId || priceId.includes('_here')) {
    throw new Error('Stripe usage tier price IDs not configured. Please set up Stripe products in your dashboard.');
  }

  try {
    // Import Firebase Functions
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const app = await getFirebaseApp();
    const functions = getFunctions(app);
    
    // Call the Cloud Function
    const createUsageTierCheckoutFn = httpsCallable(functions, 'createUsageTierCheckoutSession');
    const result = await createUsageTierCheckoutFn({
      priceId,
      tierId: tier,
      runs: USAGE_TIERS[tier].runs,
      successUrl: `${window.location.origin}/pricing/success?tier=${tier}`,
      cancelUrl: `${window.location.origin}/pricing?canceled=true`,
    });

    const data = result.data as { sessionId: string; url: string };
    
    // Redirect to Stripe Checkout using the URL
    if (data.url) {
      window.location.href = data.url;
    } else if (data.sessionId) {
      // Fallback: use Stripe.js to redirect
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }

      const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
      
      if (error) {
        throw error;
      }
    }
  } catch (error) {
    logger.error('Error creating usage tier checkout session', { error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
};

/**
 * Get usage status from Firestore
 */
export const getUserUsage = async (userId: string): Promise<UsageData | null> => {
  try {
    const { module, db } = await getFirestoreDeps();
    const usageRef = module.doc(db, 'users', userId, 'usage', 'current');
    const usageSnap = await module.getDoc(usageRef);
    
    if (!usageSnap.exists()) {
      return null;
    }
    
    return usageSnap.data() as UsageData;
  } catch (error) {
    logger.error('Error fetching usage status', { error: error instanceof Error ? error.message : String(error), userId });
    return null;
  }
};

/**
 * Subscribe to real-time usage updates
 */
export const subscribeToUsageUpdates = (
  userId: string,
  callback: (usage: UsageData | null) => void
): Unsubscribe => {
  let unsubscribe: Unsubscribe | null = null;

  const start = async () => {
    const { module, db } = await getFirestoreDeps();
    const usageRef = module.doc(db, 'users', userId, 'usage', 'current');
    unsubscribe = module.onSnapshot(
      usageRef,
      snapshot => {
        if (snapshot.exists()) {
          callback(snapshot.data() as UsageData);
        } else {
          callback(null);
        }
      },
      error => {
        logger.error('Error subscribing to usage updates', { error: error instanceof Error ? error.message : String(error) });
        callback(null);
      }
    );
  };

  start().catch(error => {
    logger.error('Failed to subscribe to usage updates', { error: error instanceof Error ? error.message : String(error) });
    callback(null);
  });

  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
};
